/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/components/button */
import React from 'react';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss';

/**
 * @typedef buttonPropsType
 * @property {string} [title=''] - Button title.
 * @property {boolean} [disabled=false] - Button is disabled.
 * @property {Function} [onClick=null] - Callback for click event.
 * @property {Function} [onClickEnabled=null] - Callback for clickEnabled event.
 * @property {Function} [onClickDisabled=null] - Callback for clickDisabled event.
 * @property {Function} [onTouchStart=null] - Callback for touchStart event.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @classdesc A simple button component.
 */
var Button = /*#__PURE__*/function (_React$Component) {
  _inherits(Button, _React$Component);

  var _super = _createSuper(Button);

  /**
   * @description Constructor.
   * @param {buttonPropsType} [props] - Component props.
   */
  function Button(props) {
    var _context;

    var _this;

    _classCallCheck(this, Button);

    _this = _super.call(this, props); // BIND METHODS

    _assertThisInitialized(_this).onClickHandler = _bindInstanceProperty(_context = _this.onClickHandler).call(_context, _assertThisInitialized(_this));
    return _this;
  }
  /**
   * @instance
   * @description Callback for click event.
   * @param {Object<string,*>} e - Triggering event.
   * @returns {void} Void.
   */


  _createClass(Button, [{
    key: "onClickHandler",
    value: function onClickHandler(e) {
      if (typeof this.props.onClick === 'function') {
        this.props.onClick(e);
      }

      if (this.props.disabled) {
        if (typeof this.props.onClickDisabled === 'function') {
          this.props.onClickDisabled(e);
        }
      } else {
        if (typeof this.props.onClickEnabled === 'function') {
          this.props.onClickEnabled(e);
        }
      }
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this2 = this,
          _context2;

      var _this$props = this.props,
          title = _this$props.title,
          disabled = _this$props.disabled,
          href = _this$props.href,
          onTouchStart = _this$props.onTouchStart,
          className = _this$props.className,
          style = _this$props.style,
          customStyles = _this$props.customStyles,
          customClassNames = _this$props.customClassNames,
          globalTheme = _this$props.globalTheme;
      var globalStyles = globalTheme ? Button.globalStyles[globalTheme] || {} : {};
      var globalClassNames = globalTheme ? Button.globalClassNames[globalTheme] || {} : {};
      var mergedStyles = {
        button: mergeStyles([globalStyles.root, !href], globalStyles.button, [globalStyles.buttonDisabled, disabled], [globalStyles.buttonEnabled, !disabled], [customStyles.root, !href], customStyles.button, [customStyles.buttonDisabled, disabled], [customStyles.buttonEnabled, !disabled], [style, !href]),
        anchor: href ? mergeStyles(globalStyles.root, customStyles.root, style) : undefined
      };
      var mergedClassNames = {
        button: mergeClassNames([resetClassNames.root, !href], [componentClassNames.buttonEnabled, !disabled], [componentClassNames.buttonDisabled, disabled], [globalClassNames.root, !href], globalClassNames.button, [globalClassNames.buttonDisabled, disabled], [globalClassNames.buttonEnabled, !disabled], [customClassNames.root, !href], customClassNames.button, [customClassNames.buttonDisabled, disabled], [customClassNames.buttonEnabled, !disabled], [className, !href]),
        anchor: href ? mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className) : undefined
      };
      var ButtonComponent = /*#__PURE__*/React.createElement("button", {
        type: 'button',
        onClick: this.onClickHandler,
        onTouchStart: _bindInstanceProperty(_context2 = function _context2(e) {
          _newArrowCheck(this, _this2);

          onTouchStart && onTouchStart(e);
        }).call(_context2, this),
        style: mergedStyles.button,
        className: mergedClassNames.button
      }, title);
      return href ? /*#__PURE__*/React.createElement("a", {
        tabIndex: -1,
        href: href,
        target: '_blank',
        rel: 'noopener noreferrer',
        style: mergedStyles.anchor,
        className: mergedClassNames.anchor
      }, ButtonComponent) : ButtonComponent;
    }
  }]);

  return Button;
}(React.Component);

_defineProperty(Button, "globalStyles", {
  default: {
    root: {},
    button: {},
    buttonEnabled: {},
    buttonDisabled: {}
  }
});

_defineProperty(Button, "globalClassNames", {
  default: {
    root: '',
    button: '',
    buttonEnabled: '',
    buttonDisabled: ''
  }
});

_defineProperty(Button, "defaultProps", {
  title: '',
  disabled: false,
  href: null,
  onClick: null,
  onClickEnabled: null,
  onClickDisabled: null,
  onTouchStart: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
});

export { Button };